import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import AnalyticsContext from './AnalyticsContext';
// @ts-ignore: no typing available
import analytics from 'analytics.js';
import { captureException, withScope } from '@sentry/browser';
import config from 'config';

interface Props {
  children: React.ReactNode;
  appUser: any;
  client: any;
}

/**
 * A provider for accessing Segment's analytics object from the window.
 *
 * Segments Docs
 * https://segment.com/docs/sources/website/analytics.js
 *
 * Segments React Setup
 * https://github.com/segmentio/analytics-react
 *
 */
class AnalyticsProvider extends React.Component<Props> {
  componentDidMount() {
    if (!_isEmpty(this.props.appUser)) {
      this.identify();
      this.msIdentify();
    }
  }

  componentDidUpdate(previousProps: any) {
    if (previousProps.appUser !== this.props.appUser) {
      this.identify();
      this.msIdentify();
    }
  }

  msIdentify = () => {
    const { appUser, client } = this.props;
    const isTestingSession = window.location.hostname.includes('localhost');
    // (note - pearl) Not tracking with Microsoft Clarity on localhost or Cypress testing
    if (isTestingSession) return;

    (window as any).clarity(
      'identify',
      appUser.id, // Clarity custom-id
      client.id, // Clarity session-id
      null, // Clarity custom-page-id
      `${appUser.firstName} ${appUser.lastName}`, // Clarity friendly-name
    );
  };

  identify = () => {
    const { appUser, client } = this.props;

    let trackers = {};
    if (config.ANALYTICS) {
      // (note - Pearl) This is handling Intercom data sending
      for (let moduleName in config.ANALYTICS) {
        const module = _get(config, ['ANALYTICS', moduleName]);

        if (module) {
          // @ts-ignore
          trackers[module.ajsName] = module.options;
        }
      }

      analytics.initialize(trackers);
      analytics.identify(appUser.id, {
        id: appUser.id,
        userId: appUser.id,
        email: appUser.email,
        name: `${appUser.firstName} ${appUser.lastName}`,
        firstName: appUser.firstName,
        lastName: appUser.lastName,
        title: appUser.title,
        company: {
          id: client.subdomain,
          name: client.displayName,
          subdomain: client.subdomain,
        },
      });
    }
  };

  track = (event: any, properties: any) => {
    const { client } = this.props;

    try {
      // @ts-ignore
      window.analytics.track(event, {
        ...properties,
        company: {
          id: client.subdomain,
          name: client.displayName,
          subdomain: client.subdomain,
        },
      });
    } catch (e) {
      withScope(scope => {
        scope.setTag('Non-fatal', 'true');
        captureException(e);
      });
    }
  };

  page = (name: any, properties: any) => {
    const { client } = this.props;

    try {
      // @ts-ignore
      window.analytics.page(name, {
        ...properties,
        company: {
          id: client.subdomain,
          name: client.displayName,
          subdomain: client.subdomain,
        },
      });
    } catch (e) {
      withScope(scope => {
        scope.setTag('Non-fatal', 'true');
        captureException(e);
      });
    }
  };

  render() {
    return (
      // @ts-ignore
      <AnalyticsContext.Provider value={this}>{this.props.children}</AnalyticsContext.Provider>
    );
  }
}

export default AnalyticsProvider;
